/**
 * Vertical Marketplace — Platform Admin view
 *
 * Shows all industry verticals defined in VERTICAL_MODULES and lets the
 * platform admin enable / disable each vertical per organisation.
 *
 * Data flow:
 *  - Organisations fetched from  GET /api/organizations
 *  - Vertical toggle saved via   PUT /api/organizations/:id  { enabled_modules: [...] }
 *
 * Consistent naming: "verticals" everywhere (not "modules").
 */

import { useState } from 'react';
import { useAuthStore } from '../../store/authStore';
import * as Icons from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Alert from '../../components/common/Alert';
import Spinner from '../../components/common/Spinner';
import api from '../../services/api';
import { VERTICAL_MODULES, VerticalModule } from '../../config/navigation.config';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface Org {
  id: string;
  name: string;
  status: string;
  enabled_modules: string[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Icon helper — resolves icon name string → Lucide component
// ─────────────────────────────────────────────────────────────────────────────
function VerticalIcon({ name, className }: { name: string; className?: string }) {
  const Icon = (Icons as any)[name] as React.ElementType | undefined;
  if (!Icon) return <Icons.Package className={className} />;
  return <Icon className={className} />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Status badge for a vertical
// ─────────────────────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: VerticalModule['status'] }) {
  if (status === 'core') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 font-medium">
        <Icons.Lock className="w-3 h-3" />
        Core
      </span>
    );
  }
  if (status === 'coming_soon') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 font-medium">
        Soon
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 font-medium">
      <Icons.CheckCircle className="w-3 h-3" />
      Available
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-vertical detail panel shown on the right side when a vertical is selected
// ─────────────────────────────────────────────────────────────────────────────
function VerticalDetailPanel({
  vertical,
  orgs,
  onToggle,
  isPending,
  onClose,
}: {
  vertical: VerticalModule;
  orgs: Org[];
  onToggle: (orgId: string, orgModules: string[], verticalId: string) => void;
  isPending: boolean;
  onClose: () => void;
}) {
  const enabledCount = orgs.filter((o) => o.enabled_modules.includes(vertical.id)).length;

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-white dark:bg-gray-900 shadow-2xl z-50 overflow-y-auto border-l border-gray-200 dark:border-gray-800">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <VerticalIcon name={vertical.icon} className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                {vertical.label.split('—')[0].trim()}
              </h2>
              {vertical.label.includes('—') && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {vertical.label.split('—')[1].trim()}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            aria-label="Close panel"
          >
            <Icons.X className="w-5 h-5" />
          </button>
        </div>

        {/* Description */}
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
          {vertical.description}
        </p>

        {/* Status + key */}
        <div className="flex items-center gap-3 mb-6">
          <StatusBadge status={vertical.status} />
          <span className="text-xs font-mono text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded">
            {vertical.id}
          </span>
        </div>

        {/* Summary stat */}
        <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg mb-6">
          <p className="text-sm text-gray-500 dark:text-gray-400">Enabled for</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white mt-0.5">
            {enabledCount} <span className="text-sm font-normal text-gray-500 dark:text-gray-400">/ {orgs.length} organisations</span>
          </p>
        </div>

        {/* Organisation toggle list */}
        {vertical.status === 'core' ? (
          <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-500 dark:text-gray-400">
            <Icons.Lock className="w-4 h-4 inline mr-1.5 mb-0.5" />
            This is a core platform module — it is always enabled for all organisations and cannot be toggled.
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              Organisations
            </p>
            {orgs.map((org) => {
              const enabled = org.enabled_modules.includes(vertical.id);
              return (
                <div
                  key={org.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icons.Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {org.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        ID: {org.id.slice(0, 8)}…
                      </p>
                    </div>
                  </div>
                  <button
                    disabled={isPending || vertical.status === 'coming_soon'}
                    onClick={() => onToggle(org.id, org.enabled_modules, vertical.id)}
                    className={[
                      'relative flex-shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                      enabled
                        ? 'bg-primary-600'
                        : 'bg-gray-200 dark:bg-gray-700',
                      isPending || vertical.status === 'coming_soon'
                        ? 'opacity-50 cursor-not-allowed'
                        : 'cursor-pointer',
                    ].join(' ')}
                    title={
                      vertical.status === 'coming_soon'
                        ? 'This vertical is not yet available'
                        : enabled
                        ? `Disable ${vertical.label.split('—')[0].trim()} for ${org.name}`
                        : `Enable ${vertical.label.split('—')[0].trim()} for ${org.name}`
                    }
                  >
                    <span
                      className={[
                        'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform',
                        enabled ? 'translate-x-5' : 'translate-x-0',
                      ].join(' ')}
                    />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export default function ModuleMarketplace() {
  const [selectedVertical, setSelectedVertical] = useState<VerticalModule | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { currentOrganizationId, setOrganizationModules } = useAuthStore();

  // ── Data ──────────────────────────────────────────────────────────────────
  const {
    data: orgs,
    isLoading,
    error: orgsError,
  } = useQuery<Org[]>({
    queryKey: ['orgs-for-verticals'],
    queryFn: async () => {
      const res = await api.get('/organizations');
      // Normalize: ensure enabled_modules is always an array
      return (res.data.data as Org[]).map((o) => ({
        ...o,
        enabled_modules: o.enabled_modules ?? ['mvp0_core', 'mvp0_enterprise'],
      }));
    },
  });

  // ── Toggle mutation — PATCH enabled_modules on the org ────────────────────
  const toggleMutation = useMutation({
    mutationFn: async ({
      orgId,
      newModules,
    }: {
      orgId: string;
      newModules: string[];
    }) => {
      await api.put(`/organizations/${orgId}`, { enabled_modules: newModules });
    },
    onSuccess: (_data, { orgId, newModules }) => {
      // Optimistic cache update
      queryClient.setQueryData<Org[]>(['orgs-for-verticals'], (old) =>
        (old ?? []).map((o) => (o.id === orgId ? { ...o, enabled_modules: newModules } : o))
      );
      // Live-update the sidebar for the current session when toggling the active org
      if (orgId === currentOrganizationId) {
        setOrganizationModules(newModules);
      }
      setSuccessMsg('Vertical settings saved.');
      setTimeout(() => setSuccessMsg(null), 3000);
    },
    onError: (err: any) => {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        'Failed to update vertical settings.';
      setErrorMsg(msg);
      setTimeout(() => setErrorMsg(null), 5000);
    },
  });

  const handleToggle = (orgId: string, currentModules: string[], verticalId: string) => {
    const newModules = currentModules.includes(verticalId)
      ? currentModules.filter((m) => m !== verticalId)
      : [...currentModules, verticalId];
    toggleMutation.mutate({ orgId, newModules });
  };

  // ── Derived stats ──────────────────────────────────────────────────────────
  const orgList = orgs ?? [];
  const purchasableVerticals = VERTICAL_MODULES.filter((v) => v.status !== 'core');
  const enabledCount = purchasableVerticals.reduce(
    (sum, v) => sum + orgList.filter((o) => o.enabled_modules.includes(v.id)).length,
    0
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (orgsError) {
    return (
      <Alert type="error">
        Failed to load organisations. Please refresh the page.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Vertical Marketplace
        </h1>
        <p className="mt-1 text-gray-600 dark:text-gray-300">
          Enable industry verticals for each organisation. Each vertical unlocks the complete
          9-step satellite → drone → remediation intelligence loop for that industry.
        </p>
      </div>

      {/* ── Inline alerts ─────────────────────────────────────────────────── */}
      {successMsg && (
        <Alert type="success" onClose={() => setSuccessMsg(null)}>
          {successMsg}
        </Alert>
      )}
      {errorMsg && (
        <Alert type="error" onClose={() => setErrorMsg(null)}>
          {errorMsg}
        </Alert>
      )}

      {/* ── Summary stats ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
              <Icons.LayoutGrid className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Purchasable Verticals</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {purchasableVerticals.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
              <Icons.Building2 className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Organisations</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {orgList.length}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <Icons.Zap className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Active Assignments</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {enabledCount}
              </p>
            </div>
          </div>
        </Card>
      </div>

      {/* ── Vertical cards grid ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {VERTICAL_MODULES.map((vertical) => {
          const orgsWithVertical = orgList.filter((o) =>
            o.enabled_modules.includes(vertical.id)
          );
          const isCore = vertical.status === 'core';
          const isComingSoon = vertical.status === 'coming_soon';
          const isSelected = selectedVertical?.id === vertical.id;

          return (
            <button
              key={vertical.id}
              type="button"
              onClick={() => setSelectedVertical(isSelected ? null : vertical)}
              className={[
                'relative text-left w-full p-5 rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                isSelected
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10 shadow-md'
                  : isCore
                  ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                  : isComingSoon
                  ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 opacity-70'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm',
              ].join(' ')}
            >
              {/* Top row: icon + status badge */}
              <div className="flex items-start justify-between mb-3">
                <div
                  className={[
                    'w-10 h-10 rounded-lg flex items-center justify-center',
                    isCore
                      ? 'bg-gray-200 dark:bg-gray-700'
                      : isSelected
                      ? 'bg-primary-100 dark:bg-primary-800/40'
                      : 'bg-gray-100 dark:bg-gray-800',
                  ].join(' ')}
                >
                  <VerticalIcon
                    name={vertical.icon}
                    className={[
                      'w-5 h-5',
                      isCore
                        ? 'text-gray-500 dark:text-gray-400'
                        : isSelected
                        ? 'text-primary-600 dark:text-primary-400'
                        : 'text-gray-500 dark:text-gray-400',
                    ].join(' ')}
                  />
                </div>
                <StatusBadge status={vertical.status} />
              </div>

              {/* Label */}
              <p className="text-sm font-semibold text-gray-900 dark:text-white leading-snug mb-1">
                {vertical.label.split('—')[0].trim()}
              </p>
              {vertical.label.includes('—') && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  {vertical.label.split('—')[1].trim()}
                </p>
              )}

              {/* Description (2-line clamp) */}
              <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 mb-4">
                {vertical.description}
              </p>

              {/* Footer: enabled-org count */}
              {isCore ? (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  Always enabled · {orgList.length} organisations
                </p>
              ) : isComingSoon ? (
                <p className="text-xs text-blue-500 dark:text-blue-400">Coming soon</p>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {orgsWithVertical.length}
                    </span>
                    {' '}of {orgList.length} org{orgList.length !== 1 ? 's' : ''} enabled
                  </p>
                  <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                    {isSelected ? 'Close ↗' : 'Manage →'}
                  </span>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Organisation × Vertical matrix ───────────────────────────────────── */}
      {orgList.length > 0 && (
        <Card>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            Vertical Assignment Matrix
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 pr-4 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap">
                    Organisation
                  </th>
                  {purchasableVerticals.map((v) => (
                    <th
                      key={v.id}
                      className="text-center px-3 py-2 font-medium text-gray-600 dark:text-gray-400 whitespace-nowrap min-w-[80px]"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <VerticalIcon name={v.icon} className="w-4 h-4" />
                        <span className="text-xs">{v.label.includes(' — ') ? v.label.split(' — ')[1] : v.label}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {orgList.map((org) => (
                  <tr key={org.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="py-2.5 pr-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Icons.Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900 dark:text-white truncate max-w-[160px]">
                          {org.name}
                        </span>
                      </div>
                    </td>
                    {purchasableVerticals.map((v) => {
                      const enabled = org.enabled_modules.includes(v.id);
                      const isComingSoon = v.status === 'coming_soon';
                      return (
                        <td key={v.id} className="text-center px-3 py-2.5">
                          <button
                            disabled={toggleMutation.isPending || isComingSoon}
                            onClick={() => handleToggle(org.id, org.enabled_modules, v.id)}
                            className={[
                              'inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors',
                              enabled
                                ? 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                                : 'bg-gray-100 text-gray-400 dark:bg-gray-800 dark:text-gray-600 hover:bg-gray-200 dark:hover:bg-gray-700',
                              toggleMutation.isPending || isComingSoon
                                ? 'opacity-50 cursor-not-allowed'
                                : 'cursor-pointer',
                            ].join(' ')}
                            title={
                              isComingSoon
                                ? 'Coming soon'
                                : enabled
                                ? `Disable ${v.label.split('—')[0].trim()} for ${org.name}`
                                : `Enable ${v.label.split('—')[0].trim()} for ${org.name}`
                            }
                          >
                            {enabled ? (
                              <Icons.Check className="w-4 h-4" />
                            ) : (
                              <Icons.Minus className="w-4 h-4" />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* ── Vertical detail side panel ────────────────────────────────────────── */}
      {selectedVertical && (
        <VerticalDetailPanel
          vertical={selectedVertical}
          orgs={orgList}
          onToggle={handleToggle}
          isPending={toggleMutation.isPending}
          onClose={() => setSelectedVertical(null)}
        />
      )}
    </div>
  );
}

// Made with Bob
